/**
 * 「シーンごとに制作」3択統合 (P2) の共通導出関数群。
 *
 * docs/features/scene-location-and-production-mode-plan.md §3.1/§3.2/§3.3/§3.3.1。
 * BE 対の実装は backend/services/scenario/generation/production_mode.py
 * (同一規則。FE/BE の規則同一性は videoGenerationMode.ts で確立した既存規約)。
 *
 * P2 step 1 時点では既存呼び出し箇所の置き換えは行わない (関数追加のみ・挙動不変)。
 * 既存の `deriveVideoGenerationMode` (シナリオ単位・3値) は変更せず、レガシー値
 * 読み込み専用として引き続き使う。
 */

import { deriveVideoGenerationMode } from './videoGenerationMode';

/** シナリオ単位の制作方式 (3.1)。 */
export type ProductionMode = 'storyboard' | 'per_scene';

/** per_scene シナリオの優先モード (3.3)。「シーンごとに制作」の初期値導出に使う。 */
export type PerSceneDefaultMode = 'frame' | 'auto';

/** シーン単位の入力方式 (3.2 の3択 + 'still')。
 *  'still' = 静止画をそのまま使う (動画を生成せず、image_material_id の静止画を出力の比率に
 *  contain で収め、余白を still_background_color で塗って表示する)。明示値でのみ成立し、
 *  デフォルト導出からは出てこない。動画素材が紐付いていれば (stillSceneVideoMaterialId)
 *  静止画の代わりにその動画を同じ規則 (contain + 余白の色) でそのまま差し込む。 */
export type SceneVideoInputMode = 'frame' | 'extend' | 'auto' | 'still' | 'motion';

/** 「静止画をそのまま使う」シーンの余白の既定色 (#RRGGBB)。BE production_mode.py と同値。 */
export const DEFAULT_STILL_BACKGROUND_COLOR = '#FFFFFF';
const STILL_BACKGROUND_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

/** 余白の色の入力を '#RRGGBB' (大文字) に正規化する。不正 / 空は null。 */
export function normalizeStillBackgroundColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return STILL_BACKGROUND_COLOR_PATTERN.test(text) ? text.toUpperCase() : null;
}

/** 「静止画をそのまま使う」シーンか (video_input_mode='still' の明示値のみ。BE is_still_image_scene と同一規則)。
 *  このシーンは動画生成の対象外。紐付いた動画素材 (ドロップ / テイク選択) は生成せずそのまま使う
 *  (stillSceneVideoMaterialId)。 */
export function isStillImageScene(scene: { video_input_mode?: string | null }): boolean {
  return scene.video_input_mode === 'still';
}

/** 「静止画をそのまま使う」シーンがそのまま差し込む動画素材の ID (BE still_scene_video_material_id と同一規則)。
 *  still シーンに動画素材が紐付いていて「採用しない」(video_excluded) でなければその ID。それ以外は null
 *  (= 静止画 image_material_id を使う)。動画も静止画と同じく出力の比率に contain で収め、余白を
 *  still_background_color で塗る (動画エディタ mapper / レンダラー / BE 合成 mp4_composer)。
 *  「採用しない」にすると静止画へ戻る (動画の紐付けは保持したまま)。 */
export function stillSceneVideoMaterialId(
  scene: { video_input_mode?: string | null; video_material_id?: string | null; video_excluded?: boolean | null },
): string | null {
  if (!isStillImageScene(scene)) return null;
  if (scene.video_excluded) return null;
  return scene.video_material_id || null;
}

/** 「モーショングラフィックスで作る」シーンか (video_input_mode='motion' の明示値のみ。BE is_motion_scene と同一規則。
 *  docs/features/brand-cut-motion-graphics-plan.md §10.4)。動画 AI は呼ばず、POST /jobs/scene/motion (定額) が
 *  Claude の書いた Remotion コードを隔離レンダラーで MP4 にして video_material_id に付ける。 */
export function isMotionScene(scene: { video_input_mode?: string | null }): boolean {
  return scene.video_input_mode === 'motion';
}

/** モーションシーンの BGM がまだ無い (未生成かつ「採用しない」でもない) 判定。生成のゲートではなく注意文の表示に使う
 *  (2026-09-28 決定: BGM 無しでも生成できる。後から BGM を付けると同期の鮮度判定が再生成を促す・§15.3)。 */
export function isMotionBgmPending(scenario: { bgm_material_id?: string | null; bgm_excluded?: boolean | null } | null | undefined): boolean {
  return !scenario?.bgm_material_id && !scenario?.bgm_excluded;
}

/** 静止画シーンの余白の色 (#RRGGBB)。未設定 / 不正値は既定の白。 */
export function resolveStillBackgroundColor(scene: { still_background_color?: string | null }): string {
  return normalizeStillBackgroundColor(scene.still_background_color) ?? DEFAULT_STILL_BACKGROUND_COLOR;
}

/** 動画生成リクエスト内の実効値 (3.1。移行中は3値のまま維持)。 */
export type EffectiveVideoGenerationMode = 'frame' | 'reference' | 'storyboard';

export interface ProductionModeScenarioSource {
  frame_mode?: string | null;
  generation_metadata?: Record<string, unknown> | null;
}

/** deriveSceneVideoInputMode が読むシーンの最小構造 (structural typing)。 */
export interface SceneVideoInputModeSceneSource {
  order_number: number;
  location_key?: string | null;
  continues_previous_shot?: boolean | null;
  brand_cut_position?: 'start' | 'end' | null;
  link_previous_video?: boolean | null;
  video_input_mode?: string | null;
}

export interface SceneVideoInputModeScenarioSource extends ProductionModeScenarioSource {
  scenes?: SceneVideoInputModeSceneSource[] | null;
}

/** シナリオ単位の制作方式を導出する ('storyboard' | 'per_scene')。
 *
 * 正本は `generation_metadata.production_mode`。無い/不正な旧データのみ、
 * 既存の `deriveVideoGenerationMode` (レガシー3値) から導出する
 * (storyboard → storyboard、frame/reference → per_scene。§5)。 */
export function deriveProductionMode(scenario: ProductionModeScenarioSource): ProductionMode {
  const gmMode = scenario.generation_metadata?.production_mode;
  if (gmMode === 'storyboard' || gmMode === 'per_scene') return gmMode;
  return deriveVideoGenerationMode(scenario) === 'storyboard' ? 'storyboard' : 'per_scene';
}

/** per_scene シナリオの優先モードを導出する ('frame' | 'auto')。
 *
 * 正本は `generation_metadata.per_scene_default_mode`。無い/不正な旧データのみ、
 * レガシー3値モードから導出する (旧 'reference' → 'auto'、'frame'/未設定 → 'frame'。§5)。 */
export function derivePerSceneDefaultMode(scenario: ProductionModeScenarioSource): PerSceneDefaultMode {
  const gmMode = scenario.generation_metadata?.per_scene_default_mode;
  if (gmMode === 'frame' || gmMode === 'auto') return gmMode;
  return deriveVideoGenerationMode(scenario) === 'reference' ? 'auto' : 'frame';
}

/** 直前の「本編シーン」(ブランドカットでない・order_number が最大の先行シーン) を返す。
 *  無ければ null (先行する本編シーンが存在しない = シーン1相当)。 */
function findPrecedingSubstantiveScene(
  scenes: SceneVideoInputModeSceneSource[],
  orderNumber: number,
): SceneVideoInputModeSceneSource | null {
  const preceding = scenes.filter((s) => (
    s.order_number < orderNumber
    && s.brand_cut_position !== 'start'
    && s.brand_cut_position !== 'end'
  ));
  if (preceding.length === 0) return null;
  return preceding.reduce((max, s) => (s.order_number > max.order_number ? s : max));
}

export interface SceneVideoInputModeResult {
  mode: SceneVideoInputMode;
  /** continues_previous_shot=true だが直前の本編シーンと location_key が
   *  食い違う矛盾データ (両方 location_key 設定済みで不一致のときのみ true)。
   *  この場合 mode は 'extend' にならず優先モードへフォールバックする。
   *  編集 UI の警告表示に使う (§3.3)。 */
  locationMismatch: boolean;
  /** 直前の本編シーンがモーショングラフィックス ('motion') / 静止画 ('still') なので延長できず、
   *  明示値 / 旧 link_previous_video / 場面継続の導出が 'extend' になるところを優先モードへ落としたときその種別。
   *  それ以外は null (BE production_mode.extend_blocked と同一規則・brand-cut-motion-graphics-plan.md §10.4「場面継続」)。 */
  extendBlocked: ExtendBlockingInputMode | null;
}

/** 延長元になれない制作方式 (直前のシーンがこれなら「延長」は成立しない)。 */
export type ExtendBlockingInputMode = 'motion' | 'still';

/** 直前の本編シーンの制作方式が延長元になれない ('motion' / 'still') ならその種別、なければ null。
 *  モーショングラフィックスは最終フレーム素材を作らず、動画も文字と図形なので実写の延長元にならない。静止画も同じ。
 *  ブランドカットは従来どおり飛ばして探す (BE extend_blocked_by_previous と同一規則)。 */
export function extendBlockedByPrevious(
  scenes: readonly SceneVideoInputModeSceneSource[],
  orderNumber: number,
): ExtendBlockingInputMode | null {
  const prev = findPrecedingSubstantiveScene([...scenes], orderNumber);
  if (!prev) return null;
  return prev.video_input_mode === 'motion' || prev.video_input_mode === 'still' ? prev.video_input_mode : null;
}

/** 延長できない理由の文言 (延長オプションの無効化・警告表示で共用)。 */
export function extendBlockedReasonText(blocked: ExtendBlockingInputMode): string {
  return blocked === 'motion'
    ? '前のシーンがモーショングラフィックスのため延長できません'
    : '前のシーンが静止画のため延長できません';
}

function result(
  mode: SceneVideoInputMode,
  extra: { locationMismatch?: boolean; extendBlocked?: ExtendBlockingInputMode | null } = {},
): SceneVideoInputModeResult {
  return { mode, locationMismatch: extra.locationMismatch ?? false, extendBlocked: extra.extendBlocked ?? null };
}

/** deriveSceneVideoInputMode の詳細版。矛盾データ検知 (locationMismatch) を
 *  併せて返す。FE の編集 UI 警告表示・deriveSceneVideoInputMode 双方から使う。 */
export function deriveSceneVideoInputModeDetailed(
  scenario: SceneVideoInputModeScenarioSource,
  scene: SceneVideoInputModeSceneSource,
): SceneVideoInputModeResult {
  const priorityMode = derivePerSceneDefaultMode(scenario);
  // 「延長」は直前の本編シーンがモーショングラフィックス / 静止画のとき成立しない。明示値・旧 link_previous_video・
  // 場面継続の導出のどれで extend になる場合でも優先モードへ落とし、extendBlocked に種別を返す。
  const blocked = extendBlockedByPrevious(scenario.scenes ?? [], scene.order_number);

  // 優先順位 1: 明示値 (§3.3.1)。'still' (静止画をそのまま使う) / 'motion' (モーショングラフィックス) は明示値でのみ成立する。
  if (scene.video_input_mode === 'extend' && blocked) {
    return result(priorityMode, { extendBlocked: blocked });
  }
  if (
    scene.video_input_mode === 'frame' || scene.video_input_mode === 'extend'
    || scene.video_input_mode === 'auto' || scene.video_input_mode === 'still'
    || scene.video_input_mode === 'motion'
  ) {
    return result(scene.video_input_mode);
  }

  // 優先順位 2/3: 旧 link_previous_video 明示値 (§3.3.1)
  if (scene.link_previous_video === true) {
    return blocked ? result(priorityMode, { extendBlocked: blocked }) : result('extend');
  }
  if (scene.link_previous_video === false) {
    return result(priorityMode);
  }

  // 優先順位 4: デフォルト導出 (§3.3)。ブランドカットシーン自体は常に優先モード
  // (延長対象外)。
  if (scene.brand_cut_position === 'start' || scene.brand_cut_position === 'end') {
    return result(priorityMode);
  }

  const prev = findPrecedingSubstantiveScene(scenario.scenes ?? [], scene.order_number);
  if (!prev) {
    // 先行する本編シーンなし (シーン1相当) → 優先モード
    return result(priorityMode);
  }

  if (scene.continues_previous_shot !== true) {
    return result(priorityMode);
  }

  const currentKey = scene.location_key ?? null;
  const prevKey = prev.location_key ?? null;
  const eitherUnset = currentKey === null || prevKey === null;
  if (eitherUnset || currentKey === prevKey) {
    return blocked ? result(priorityMode, { extendBlocked: blocked }) : result('extend');
  }
  // continues_previous_shot=true だが両方 location_key 設定済みで不一致 = 矛盾データ。
  // 延長せず優先モードへ (警告フラグを立てる)。
  return result(priorityMode, { locationMismatch: true });
}

/** シーンの実効入力方式を導出する ('frame' | 'extend' | 'auto')。詳細 (矛盾検知) が
 *  不要な呼び出し向けの簡易版。docs/features/scene-location-and-production-mode-plan.md
 *  §3.3.1 の優先順位 (1: 明示値 → 2/3: 旧 link_previous_video → 4: §3.3 デフォルト導出)
 *  に従う。 */
export function deriveSceneVideoInputMode(
  scenario: SceneVideoInputModeScenarioSource,
  scene: SceneVideoInputModeSceneSource,
): SceneVideoInputMode {
  return deriveSceneVideoInputModeDetailed(scenario, scene).mode;
}

/** 動画生成リクエスト内の実効値を導出する ('frame' | 'reference' | 'storyboard')。
 *  §3.1: 移行中の既存 `video_generation_mode` (3値) はこの関数で維持したまま、
 *  per_scene シナリオではシーンの実効入力方式から詰める。 */
export function deriveEffectiveVideoGenerationMode(
  scenario: SceneVideoInputModeScenarioSource,
  scene: SceneVideoInputModeSceneSource,
): EffectiveVideoGenerationMode {
  if (deriveProductionMode(scenario) === 'storyboard') return 'storyboard';
  const inputMode = deriveSceneVideoInputMode(scenario, scene);
  // 'still' は動画生成の対象外 (呼び出し側が isStillImageScene で先に弾く)。'motion' も動画 AI の対象外
  // (別ジョブ。呼び出し側が isMotionScene で先に分岐する)。
  // 万一リクエストが組まれても frame 系の入力 (first_frame) を要求しない 'reference' に倒す。
  return inputMode === 'frame' ? 'frame' : 'reference';
}

/** 保存済みシナリオを開いたときに FE の `videoGenerationMode`（レガシー3値の UI 表示状態。
 *  VideoFrameModeSelector の選択状態そのもの）を復元するための合成ヘルパー (§5)。
 *  `deriveProductionMode` / `derivePerSceneDefaultMode`（正本 = generation_metadata の
 *  新フィールド優先、無ければレガシー `video_generation_mode`/`frame_mode` へフォールバック）
 *  の結果を3値へ合成するだけの薄い関数。 */
export function deriveLoadedVideoGenerationMode(
  scenario: ProductionModeScenarioSource,
): 'frame' | 'reference' | 'storyboard' {
  if (deriveProductionMode(scenario) === 'storyboard') return 'storyboard';
  return derivePerSceneDefaultMode(scenario) === 'auto' ? 'reference' : 'frame';
}

/** シナリオ生成リクエストへ書き込む production_mode / per_scene_default_mode の組。
 *  per_scene_default_mode は production_mode==='storyboard' のときは無関係な値のため
 *  undefined（保存しない）。 */
export interface ProductionModeWriteFields {
  production_mode: ProductionMode;
  per_scene_default_mode: PerSceneDefaultMode | undefined;
}

/**
 * シナリオ生成フォームの `videoGenerationMode`（'frame' | 'reference' | 'storyboard' の
 * レガシー3値・フォームの実選択状態そのもの）から、新規保存する
 * `production_mode` / `per_scene_default_mode` の組を導出する
 * (docs/features/scene-location-and-production-mode-plan.md §5-2 の書き込み3点セット)。
 *
 * レガシー `video_generation_mode` 自体は呼び出し側が従来どおり送信し続ける
 * （この関数は production_mode/per_scene_default_mode の2フィールドのみを返す）。
 * `videoGenerationMode` が null/未設定/不正値のときはフォームの既定 (frame 優先) に揃える。
 */
export function deriveProductionModeWriteFields(
  videoGenerationMode: string | null | undefined,
): ProductionModeWriteFields {
  if (videoGenerationMode === 'storyboard') {
    return { production_mode: 'storyboard', per_scene_default_mode: undefined };
  }
  return {
    production_mode: 'per_scene',
    per_scene_default_mode: videoGenerationMode === 'reference' ? 'auto' : 'frame',
  };
}
